import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

interface TranslationCache {
  [key: string]: {
    [lang: string]: string;
  };
}

// In-memory cache for translations
const translationCache: TranslationCache = {};

export function useTranslatedContent() {
  const { i18n } = useTranslation();
  const [isTranslating, setIsTranslating] = useState(false);
  const currentLanguage = i18n.language;

  // Generate cache key from text
  const getCacheKey = (text: string) => {
    return text.substring(0, 100).replace(/\s+/g, "_");
  };

  // Check if translation exists in cache
  const getCachedTranslation = useCallback((text: string, lang: string): string | null => {
    const key = getCacheKey(text);
    return translationCache[key]?.[lang] || null;
  }, []);

  // Store translation in cache
  const cacheTranslation = useCallback((text: string, lang: string, translation: string) => {
    const key = getCacheKey(text);
    if (!translationCache[key]) {
      translationCache[key] = {};
    }
    translationCache[key][lang] = translation;
  }, []);

  // Translate a single text
  const translateText = useCallback(async (text: string): Promise<string> => {
    // Return original if Portuguese or empty
    if (currentLanguage === "pt" || !text || text.trim() === "") {
      return text;
    }

    // Check cache first
    const cached = getCachedTranslation(text, currentLanguage);
    if (cached) {
      return cached;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate-content`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            texts: [text],
            targetLanguage: currentLanguage,
            sourceLanguage: "pt",
          }),
        }
      );

      if (!response.ok) {
        console.error("Translation failed:", response.status);
        return text;
      }

      const data = await response.json();
      const translation = data.translations?.[0] || text;
      
      // Cache the result
      cacheTranslation(text, currentLanguage, translation);
      
      return translation;
    } catch (error) {
      console.error("Translation error:", error);
      return text;
    }
  }, [currentLanguage, getCachedTranslation, cacheTranslation]);

  // Translate multiple texts in batch
  const translateBatch = useCallback(async (texts: string[]): Promise<string[]> => {
    // Return original if Portuguese
    if (currentLanguage === "pt") {
      return texts;
    }

    // Filter out cached translations
    const uncachedTexts: string[] = [];
    const uncachedIndices: number[] = [];
    const results: string[] = [...texts];

    texts.forEach((text, index) => {
      const cached = getCachedTranslation(text, currentLanguage);
      if (cached) {
        results[index] = cached;
      } else if (text && text.trim() !== "") {
        uncachedTexts.push(text);
        uncachedIndices.push(index);
      }
    });

    // If all are cached, return immediately
    if (uncachedTexts.length === 0) {
      return results;
    }

    setIsTranslating(true);

    try {
      // Batch translations in chunks of 10
      const chunkSize = 10;
      for (let i = 0; i < uncachedTexts.length; i += chunkSize) {
        const chunk = uncachedTexts.slice(i, i + chunkSize);
        const chunkIndices = uncachedIndices.slice(i, i + chunkSize);

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate-content`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({
              texts: chunk,
              targetLanguage: currentLanguage,
              sourceLanguage: "pt",
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const translations = data.translations || [];
          
          translations.forEach((translation: string, idx: number) => {
            const originalIndex = chunkIndices[idx];
            results[originalIndex] = translation;
            cacheTranslation(texts[originalIndex], currentLanguage, translation);
          });
        }
      }
    } catch (error) {
      console.error("Batch translation error:", error);
    } finally {
      setIsTranslating(false);
    }

    return results;
  }, [currentLanguage, getCachedTranslation, cacheTranslation]);

  // Translate product data — prefers persisted columns (name_en/_es/_fr/_pl, etc.)
  // and only falls back to on-the-fly AI translation if columns are empty.
  const translateProduct = useCallback(async <T extends Record<string, any>>(
    product: T
  ): Promise<T> => {
    if (currentLanguage === "pt" || !product) {
      return product;
    }
    const lang = currentLanguage;
    const fields = ["name", "short_description", "description"] as const;
    const result: any = { ...product };
    const toTranslateTexts: string[] = [];
    const toTranslateFields: string[] = [];

    for (const f of fields) {
      const persisted = product[`${f}_${lang}`];
      if (persisted && String(persisted).trim() !== "") {
        result[f] = persisted;
        continue;
      }
      const src = product[f];
      if (src && typeof src === "string" && src.trim() !== "") {
        toTranslateTexts.push(src);
        toTranslateFields.push(f);
      }
    }

    if (toTranslateTexts.length === 0) return result;

    const translations = await translateBatch(toTranslateTexts);
    toTranslateFields.forEach((f, i) => {
      result[f] = translations[i];
    });
    return result;
  }, [currentLanguage, translateBatch]);

  // Translate array of products — uses persisted columns first, batches the rest.
  const translateProducts = useCallback(async <T extends Record<string, any>>(
    products: T[]
  ): Promise<T[]> => {
    if (currentLanguage === "pt" || products.length === 0) {
      return products;
    }
    const lang = currentLanguage;
    const fields = ["name", "short_description", "description"] as const;

    const results: any[] = products.map((p) => ({ ...p }));
    const allTexts: string[] = [];
    const mapping: { idx: number; field: string }[] = [];

    products.forEach((p, idx) => {
      for (const f of fields) {
        const persisted = (p as any)[`${f}_${lang}`];
        if (persisted && String(persisted).trim() !== "") {
          results[idx][f] = persisted;
          continue;
        }
        const src = (p as any)[f];
        if (src && typeof src === "string" && src.trim() !== "") {
          allTexts.push(src);
          mapping.push({ idx, field: f });
        }
      }
    });

    if (allTexts.length === 0) return results;

    const translations = await translateBatch(allTexts);
    translations.forEach((t, i) => {
      const { idx, field } = mapping[i];
      results[idx][field] = t;
    });
    return results;
  }, [currentLanguage, translateBatch]);

  return {
    currentLanguage,
    isPortuguese: currentLanguage === "pt",
    isTranslating,
    translateText,
    translateBatch,
    translateProduct,
    translateProducts,
  };
}
